// The device page: the UI on the main thread. It holds no SQL; every engine call
// goes to ./worker.js, which runs ./driver.js.

const $ = (id) => document.getElementById(id);

// ── the worker ─────────────────────────────────────────────────────────────
// `?memory=1` keeps the phone database in memory, as before kinedb B1215.
const MEMORY = new URLSearchParams(location.search).get('memory') === '1';
const worker = new Worker(MEMORY ? '/worker.js?memory=1' : '/worker.js', { type: 'module' });
const waiting = new Map();
let nextId = 0;
worker.onmessage = (ev) => {
  if (ev.data.event === 'change') { serverChanged(ev.data); return; }
  const { id, ok, value, error } = ev.data;
  const slot = waiting.get(id);
  if (!slot) return;
  waiting.delete(id);
  if (ok) slot.resolve(value);
  else slot.reject(Object.assign(new Error(error.message), { stage: error.stage, photos: error.photos }));
};
function call(method, ...args) {
  nextId += 1;
  const id = nextId;
  return new Promise((resolve, reject) => {
    waiting.set(id, { resolve, reject });
    worker.postMessage({ id, method, args });
  });
}

// ── state ──────────────────────────────────────────────────────────────────
/** The sync credential: {url, token} from /session. */
let cred = null;
/**
 * record id → {id, name, project, note, takenAt, size, chunks, hash, thumb, source, status}
 * One entry per RECORD: the same image saved for two projects is one photo (one
 * `name`) with two records, and the list shows both. `source` is 'phone' when the
 * local store holds the bytes, 'server' when the page shows the server's copy.
 */
const photos = new Map();
/** The page shows the newest photos only. */
const LIST_MAX = 20;
/**
 * The automated tests write to the same server, with a project that starts with
 * "TEST ". A demo hides those records; `?test=1` shows them (the browser test uses it).
 */
const SHOW_TEST = new URLSearchParams(location.search).has('test');
const visible = (project) => SHOW_TEST || !String(project ?? '').startsWith('TEST ');
/** The record id of the selected line. */
let selected = null;
let busy = false;

/**
 * "Auto sync". Off by default: the owner tests what the STORED phone database shows
 * after a reload. The choice stays in this browser only. On, the page
 *  - syncs at the start,
 *  - pushes after each save and delete,
 *  - shows each change the server sends on the live tail (`onChange` of the sync),
 *  - and syncs again when the link comes back, or every 15 s while an entry waits.
 */
const AUTOSYNC_KEY = 'docutools.autosync';
function autosync() {
  try { return localStorage.getItem(AUTOSYNC_KEY) === '1'; } catch { return false; }
}
function setAutosync(on) {
  try { localStorage.setItem(AUTOSYNC_KEY, on ? '1' : '0'); } catch { /* private window: not kept */ }
}

/** The photo name (the SHA-256 of the image) of the selected record. */
const selectedName = () => photos.get(selected)?.name ?? null;

/**
 * The image type from the photo name (`<sha256>.<ext>`). A blob without a type can make
 * a new tab download the image instead of showing it.
 */
function imageType(name) {
  const ext = String(name).slice(String(name).lastIndexOf('.') + 1).toLowerCase();
  return { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif',
    bmp: 'image/bmp', heic: 'image/heic', heif: 'image/heif' }[ext] ?? 'image/jpeg';
}

/** The image as a link: a click opens it in a new tab and does not select the line. */
function viewLink(src, cls = '') {
  return `<a class="view" href="${src}" target="_blank" rel="noopener" title="Open the image in a new tab"><img${cls ? ` class="${cls}"` : ''} src="${src}" alt=""></a>`;
}

/** Another record of the same image: it shares the image and the chunk facts. */
const sibling = (name) => [...photos.values()].find((p) => p.name === name);

function log(text, kind = '') {
  const li = document.createElement('li');
  li.className = kind;
  li.innerHTML = `<time>${new Date().toLocaleTimeString()}</time> ${text}`;
  $('device-log').prepend(li);
}

function kib(bytes) {
  return `${(bytes / 1024).toFixed(0)} KiB`;
}

// ── the phone ──────────────────────────────────────────────────────────────
async function refreshPending() {
  const entries = await call('pending');
  $('pending').textContent = entries.length === 0
    ? 'nothing waits'
    : entries.map((e) => `#${e.seq} ${e.op}`).join(' · ');
}

/** The newest photos, newest first. */
function newest() {
  return [...photos.values()]
    .sort((a, b) => String(b.takenAt).localeCompare(String(a.takenAt)))
    .slice(0, LIST_MAX);
}

/**
 * Add the records of the LOCAL database to the list: after a reload the page memory
 * is empty, but the sync pulled the rows back. The image comes from the local store
 * when it holds the bytes, and from the server when it does not.
 */
async function loadFromLocal() {
  const all = await call('localPhotos');
  // A record that is gone from the local database (deleted on the server and
  // pulled, or deleted here) leaves the list.
  const present = new Set(all.map((r) => r.id));
  for (const id of [...photos.keys()]) if (!present.has(id)) photos.delete(id);
  if (selected && !photos.has(selected)) selected = newest()[0]?.id ?? null;
  const rows = all.filter((r) => visible(r.project)).slice(0, LIST_MAX);
  // A stored record that waits for a push is "on the device only", also after a reload.
  const waiting = new Set(await call('waitingRecords'));
  for (const r of rows) {
    if (photos.has(r.id)) continue;
    const twin = sibling(r.photo);
    const bytes = twin ? null : await call('localImage', r.photo);
    photos.set(r.id, {
      id: r.id, name: r.photo, project: r.project, note: r.note, takenAt: r.takenAt,
      size: twin?.size ?? bytes?.length ?? null, chunks: twin?.chunks ?? null,
      chunkHashes: twin?.chunkHashes ?? null, hash: twin?.hash ?? '',
      thumb: twin?.thumb ?? (bytes ? URL.createObjectURL(new Blob([bytes], { type: imageType(r.photo) }))
        : `/server-photo?photo=${encodeURIComponent(r.photo)}`),
      source: twin?.source ?? (bytes ? 'phone' : 'server'),
      status: waiting.has(r.id) ? 'device' : 'server',
    });
  }
}

function renderPhotos() {
  const list = $('photos');
  list.innerHTML = '';
  for (const p of newest()) {
    const li = document.createElement('li');
    li.className = `photo ${p.id === selected ? 'selected' : ''}`;
    const facts = p.size === null
      ? `taken ${escapeHtml(String(p.takenAt).replace('T', ' ').slice(0, 16))} UTC`
      : `${kib(p.size)}${p.chunks ? ` · ${p.chunks} chunks` : ''}${p.hash ? ` · ${p.hash.slice(0, 12)}…` : ''}`;
    const remote = p.source === 'server'
      ? ' <span class="badge remote" title="The sync pull does not bring photo bytes to the phone yet (kinedb B1425). This image is the server\'s copy.">image from the server</span>'
      : '';
    li.innerHTML = `
      ${viewLink(p.thumb)}
      <div>
        <strong>${escapeHtml(p.project)}</strong> <span class="badge ${p.status}">${statusText(p.status)}</span>${remote}
        <div class="muted">${escapeHtml(p.note || '—')}</div>
        <div class="mono muted">${facts}</div>
        <button class="del del-local" type="button">Delete on phone</button>
      </div>`;
    li.onclick = () => { selected = p.id; renderPhotos(); refreshServer(); };
    li.querySelector('.del-local').onclick = (ev) => { ev.stopPropagation(); deleteLocal(p.id); };
    li.querySelector('a.view').onclick = (ev) => ev.stopPropagation();
    list.append(li);
  }
}

/**
 * Delete ONE record on the phone. The photo goes with its last record (the driver
 * does that). The delete is a sync write: the next sync deletes it on the server.
 */
async function deleteLocal(id) {
  const p = photos.get(id);
  if (!p) return;
  try {
    await call('deletePhoto', { id, name: p.name });
  } catch (err) {
    log(`The delete failed: ${escapeHtml(err.message)}`, 'err');
    return;
  }
  photos.delete(id);
  await loadFromLocal();
  if (selected === id) selected = newest()[0]?.id ?? null;
  const kept = sibling(p.name) ? ' The image stays on the phone for its other record(s).' : '';
  log(`Deleted on the phone: ${escapeHtml(p.note || p.project)}.${kept} ${p.status === 'server'
    ? 'The next sync deletes it on the server too.' : 'It was not synced, so nothing goes to the server.'}`, 'ok');
  renderPhotos();
  await refreshPending();
  await refreshServer();
  autoSync();
}

/**
 * Erase the phone database in this browser. The first click arms the button, the second
 * click erases: no browser dialog. Entries that wait are lost. The page does NOT reload
 * and does not sync (the owner, 2026-10-01): the owner's own reload is then the first
 * start of a new user, and that start syncs once.
 */
let eraseArmed = null;
async function eraseLocal() {
  const btn = $('erase');
  if (!eraseArmed) {
    btn.textContent = 'Click again to erase';
    eraseArmed = setTimeout(() => { eraseArmed = null; btn.textContent = "Erase the phone's database"; }, 4000);
    return;
  }
  clearTimeout(eraseArmed);
  eraseArmed = null;
  try {
    await call('erase');
  } catch (err) {
    log(`The erase failed: ${escapeHtml(err.message)}`, 'err');
    return;
  }
  for (const p of photos.values()) if (p.thumb.startsWith('blob:')) URL.revokeObjectURL(p.thumb);
  photos.clear();
  selected = null;
  renderPhotos();
  for (const id of ['save', 'sync', 'erase']) $(id).disabled = true;
  btn.textContent = "Erase the phone's database";
  $('pending').textContent = '—';
  $('server-photo').textContent = '—';
  $('server-verdict').textContent = '';
  $('blocks').innerHTML = '';
  $('blocks-caption').textContent = '';
  $('storage').textContent = '';
  $('status').textContent = 'The phone database is erased. Reload the page to start as a new user: that start syncs once.';
  log('The phone database is erased. Nothing was synced.', 'ok');
  await refreshList();
}

/**
 * Delete ONE record on the server (`id`), or a photo without a record (`id` null).
 * The photo goes with its last record. The next sync of the phone removes it there too.
 */
async function deleteServer(name, id, label) {
  const res = await (await fetch('/server-delete', { method: 'POST', body: JSON.stringify({ photo: name, id }) })).json();
  if (res.error) {
    log(`The delete on the server failed: ${escapeHtml(res.error)}`, 'err');
    return;
  }
  const kept = id && res.files === 0 ? ' The photo stays on the server for its other record(s).' : '';
  log(`Deleted on the server: ${escapeHtml(label)}.${kept} The next sync removes it from the phone.`, 'ok');
  await refreshServer();
}

function statusText(status) {
  return {
    device: 'on the device only', sending: 'sending…', server: 'on the server',
    missing: 'on the phone only: the server does not have it',
  }[status] ?? status;
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/**
 * The GPS position, or null after 3 s. The `timeout` option of the browser starts only
 * AFTER the permission is given, so an open permission prompt waits for ever. The
 * timer here stops the save from waiting on it.
 */
function position() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const timer = setTimeout(() => resolve(null), 3000);
    navigator.geolocation.getCurrentPosition(
      (p) => { clearTimeout(timer); resolve({ lat: p.coords.latitude, lon: p.coords.longitude }); },
      () => { clearTimeout(timer); resolve(null); },
      { timeout: 3000, maximumAge: 60_000 },
    );
  });
}

async function save(ev) {
  ev.preventDefault();
  const file = $('photo-input').files[0];
  if (!file) { log('Pick a photo first.', 'warn'); return; }
  const bytes = new Uint8Array(await file.arrayBuffer());
  const gps = (await position()) ?? { lat: 55.6761, lon: 12.5683 };
  const input = {
    bytes, fileName: file.name, project: $('project').value, note: $('note').value,
    lat: gps.lat, lon: gps.lon, takenAt: new Date().toISOString(),
  };
  try {
    const saved = await call('savePhoto', input);
    const known = sibling(saved.name);
    photos.set(saved.id, {
      ...saved,
      // A reused photo keeps the chunk facts of its first save.
      chunks: saved.chunks ?? known?.chunks ?? null,
      chunkHashes: saved.chunkHashes ?? known?.chunkHashes ?? null,
      hash: saved.hash || known?.hash || '',
      project: input.project, note: input.note, takenAt: input.takenAt,
      thumb: URL.createObjectURL(new Blob([bytes], { type: file.type || imageType(saved.name) })),
      source: 'phone', status: 'device',
    });
    selected = saved.id;
    log(saved.reused
      ? 'This image is already on the phone (the same SHA-256). The phone stored only a new record that points to it.'
      : `Saved on the device: ${kib(saved.size)} in ${saved.chunks} chunks. No network was used.`, 'ok');
    $('note').value = '';
    $('photo-input').value = '';
    renderPhotos();
    await refreshPending();
    await refreshServer();
    autoSync();
  } catch (err) {
    log(`The save failed: ${escapeHtml(err.message)}`, 'err');
  }
}

/**
 * Auto sync off: a sync with `live: false` ends the live tail of each table (kinedb B2704).
 * It is one last fill, so it brings what the server changed until now.
 */
let stopping = null;
function stopLiveTail() {
  stopping = closeLiveTail().finally(() => { stopping = null; });
  return stopping;
}
async function closeLiveTail() {
  if (!cred) return;
  // One operation at a time: wait for a push or sync that runs now.
  while (busy) await new Promise((r) => setTimeout(r, 100));
  if (autosync()) return; // the user turned it on again meanwhile
  busy = true;
  try {
    await call('start', cred, false);
    log('The live tail is closed. The phone changes only on request.', 'ok');
  } catch (err) {
    log(`Auto sync is off, but the live tail did not close: ${escapeHtml(err.message.replace(/^kinedb embedded: /, '').split(/\.\s/)[0])}.`, 'err');
  } finally {
    busy = false;
  }
}

let syncAgain = false;
/** Auto sync: sync now, or right after the sync that runs. */
function autoSync() {
  if (!autosync() || !cred) return;
  if (busy) syncAgain = true;
  else sync();
}

let changeTimer = null;
/**
 * The live tail applied a change to the local tables. The data is local already: the
 * page only reads it again. A `change` with an error means the session is gone, so the
 * page syncs again (that opens a new session).
 */
function serverChanged({ table, error }) {
  if (!autosync() || !cred) return;
  if (error) { autoSync(); return; }
  clearTimeout(changeTimer);
  changeTimer = setTimeout(async () => {
    if (busy) { syncAgain = true; return; }
    const before = photos.size;
    await loadFromLocal();
    renderPhotos();
    await refreshPending();
    await refreshServer();
    log(`The server changed ${table}. The phone has the change (live tail).${photos.size !== before ? ` Records: ${before} → ${photos.size}.` : ''}`, 'ok');
  }, 300);
}

async function sync() {
  if (stopping) await stopping; // the live tail closes first: one operation at a time
  if (busy) return;
  busy = true;
  $('sync').disabled = true;
  const sending = [...photos.values()].filter((p) => p.status !== 'server');
  for (const p of sending) p.status = 'sending';
  renderPhotos();
  const t0 = performance.now();
  // The link counters come from the proxy; the difference is what THIS sync sent.
  const chunksBefore = linkCounters?.chunksForwarded ?? 0;
  try {
    // Push the offline writes, then pull what the server holds.
    const report = await call('syncAll', cred, autosync());
    const ms = Math.round(performance.now() - t0);
    for (const p of sending) p.status = 'server';
    showReport(report, ms);
    explain(report);
    const before = photos.size;
    await loadFromLocal();
    const pulled = photos.size - before;
    $('sync').textContent = 'Sync now';
    log(`The sync finished in ${ms} ms.${pulled ? ` It brought ${pulled} record(s) from the server.` : ''}`, 'ok');
  } catch (err) {
    for (const p of sending) p.status = 'device';
    const where = err.stage === 'meta' ? 'the records' : 'the photos';
    // The last link events can arrive a moment after the socket closed.
    await new Promise((r) => setTimeout(r, 400));
    const passed = (linkCounters?.chunksForwarded ?? 0) - chunksBefore;
    if (err.stage !== 'meta') {
      log(`${passed} chunk(s) passed the link before it dropped. The server keeps every chunk that arrived.`);
    }
    // The engine's first sentence names the cause; the rest is advice for a developer.
    const cause = err.message.replace(/^kinedb embedded: /, '').split(/\.\s/)[0];
    $('sync').textContent = 'Reconnect and sync';
    log(`The sync of ${where} failed: ${escapeHtml(cause)}. The phone keeps every entry. The next sync opens a new connection.`, 'err');
    if (err.photos) showReport({ photos: err.photos, meta: null }, 0);
  } finally {
    busy = false;
    $('sync').disabled = autosync();
    renderPhotos();
    await refreshPending();
    await refreshServer();
    if (syncAgain) { syncAgain = false; autoSync(); }
  }
}

addEventListener('online', autoSync);
setInterval(async () => {
  if (!autosync() || !cred || busy) return;
  if ((await call('pending')).length > 0) autoSync();
}, 15_000);

/** Write the chunk comparison of one sync into the log, oldest step first. */
function explain({ photos: p, meta: m }) {
  if (p.chunksTotal > 0) {
    log(`The phone asked the server which of its ${p.chunksTotal} chunk(s) it already holds (FindHeldChunks).`);
    log(`The server holds ${p.chunksHeld} of the ${p.chunksTotal}. The phone sent only the other ${p.chunksSent} (${kib(p.chunkBytesSent)}).`);
  }
  // A photo and its record travel as ONE group; the server commits the group at one
  // version (kinedb B1423). The outcomes of both pushes, together:
  const outcomes = [...p.outcomes, ...(m?.outcomes ?? [])];
  const replayed = outcomes.filter((o) => o.replayed);
  const fresh = outcomes.filter((o) => o.status === 'applied' && !o.replayed);
  if (replayed.length > 0) {
    log(`The server had already applied ${replayed.length} entr${replayed.length === 1 ? 'y' : 'ies'} and answered "replayed". Nothing was stored twice.`);
  }
  if (fresh.length > 0) {
    const commits = new Set(fresh.map((o) => o.version)).size;
    log(`The server committed ${fresh.length} entr${fresh.length === 1 ? 'y' : 'ies'} (photo and record) in ${commits} commit(s): each photo and its record together.`);
  }
}

function showReport({ photos: p, meta: m }, ms) {
  const replayed = [...p.outcomes, ...(m?.outcomes ?? [])].filter((o) => o.replayed).length;
  // One box per chunk: green = already on the server, blue = sent by this sync.
  const strip = p.chunksTotal > 0
    ? `<tr><td colspan="2"><div id="chunk-strip" title="green: already on the server · blue: sent now">${
      '<span class="held"></span>'.repeat(p.chunksHeld)}${'<span class="sent"></span>'.repeat(p.chunksSent)}</div></td></tr>`
    : '';
  $('report').innerHTML = `${strip}
    <tr><th>chunks in the photos</th><td>${p.chunksTotal}</td></tr>
    <tr><th>chunks the server already held</th><td>${p.chunksHeld}</td></tr>
    <tr><th>chunks sent now</th><td>${p.chunksSent} (${kib(p.chunkBytesSent)})</td></tr>
    <tr><th>entries applied (photo + record)</th><td>${p.applied + (m?.applied ?? 0)}${replayed ? ` — <b>${replayed} replayed</b>: the server had them already` : ''}</td></tr>
    <tr><th>refused</th><td>${p.rejected + (m?.rejected ?? 0)}</td></tr>
    ${ms ? `<tr><th>time</th><td>${ms} ms</td></tr>` : ''}`;
}

// ── the server ─────────────────────────────────────────────────────────────
async function refreshServer() {
  await Promise.all([refreshSelected(), refreshList()]);
}

/**
 * The photo is on the server when its ONE photo row and at least one record are. The
 * name is the SHA-256 of the image, so the same image saved twice is one photo with
 * two records.
 */
const complete = (s) => s.photoRows === 1 && s.metaRows >= 1;

/** What the server holds for one photo, in words. */
function verdictOf(s) {
  if (complete(s)) {
    return s.metaRows === 1 ? 'The photo and its record are on the server, once.'
      : `The photo is on the server, once, with ${s.metaRows} records (the same image, saved ${s.metaRows} times).`;
  }
  if (s.photoRows === 0 && s.metaRows === 0) return 'The server holds no row of this photo yet.';
  if (s.photoRows === 1 && s.metaRows === 0) return 'The server holds the photo. The record is not sent yet.';
  return `Unexpected: ${s.photoRows} photo row(s), ${s.metaRows} record row(s).`;
}


/**
 * The warning above the lists: the local copy is `gapped` (the server could not resume
 * the phone's token), or the phone holds records the server does not. The button writes
 * the phone's rows again and fills from the server in full.
 */
async function paintGap() {
  const missing = [...photos.values()].filter((p) => p.status === 'missing').length;
  let gapped = false;
  try {
    const s = await call('linkState');
    gapped = s.photos.state === 'gapped' || s.meta.state === 'gapped';
  } catch { /* the engine is busy or erased: show nothing new */ }
  const text = [
    gapped ? 'The server history changed: the local copy of the server tables is gapped.' : '',
    missing ? `${missing} record(s) on this phone are not on the server.` : '',
  ].filter(Boolean).join(' ');
  $('gap').hidden = !text;
  $('gap-text').textContent = text;
}

/** Write the phone's missing records again, push them, and fill from the server in full. */
async function resendRows() {
  if (busy || !cred) return;
  busy = true;
  $('sync').disabled = true;
  $('resend').disabled = true;
  const ids = [...photos.values()].filter((p) => p.status === 'missing').map((p) => p.id);
  try {
    log(`Writing ${ids.length} record(s) of the phone again, then sending them…`);
    const r = await call('resend', cred, ids, autosync());
    showReport(r.pushed, 0);
    explain(r.pushed);
    log(`Sent again: ${r.sent} record(s)${r.skipped ? `; ${r.skipped} skipped (this phone has no copy of the image)` : ''}. The local copy was filled from the server in full.`, 'ok');
  } catch (err) {
    log(`Sending the rows again failed: ${escapeHtml(err.message.replace(/^kinedb embedded: /, '').split(/\.\s/)[0])}.`, 'err');
  } finally {
    busy = false;
    $('sync').disabled = autosync();
    $('resend').disabled = false;
    await loadFromLocal();
    await refreshPending();
    await refreshServer();
  }
}

/**
 * The photos the SERVER holds, read from the server itself. A change on the phone
 * does not show here until a sync sends it. The phone's names ride along, so a photo
 * of this phone that arrived without its record yet ('photo only') is listed too.
 */
async function refreshList() {
  let list;
  try {
    const res = await fetch('/server-photos', {
      method: 'POST', body: JSON.stringify({ names: [...new Set([...photos.values()].map((p) => p.name))], test: SHOW_TEST }),
    });
    list = await res.json();
    if (list.error) throw new Error(list.error);
  } catch (err) {
    $('server-summary').textContent = `The server did not answer: ${err.message}`;
    return;
  }
  // A record that the page counts as synced but the server does not hold (a reset, a
  // restore): say so. Nothing in the pull tells the phone.
  const ids = new Set(list.recordIds ?? []);
  let changed = false;
  for (const p of photos.values()) {
    const next = p.status === 'server' && !ids.has(p.id) ? 'missing'
      : p.status === 'missing' && ids.has(p.id) ? 'server' : p.status;
    if (next !== p.status) { p.status = next; changed = true; }
  }
  if (changed) renderPhotos();
  await paintGap();
  $('server-summary').textContent = list.total === 0 ? 'The server holds no photo.'
    : `The server holds ${list.photoCount} photo(s) and ${list.recordCount} record(s).${
      list.total > list.photos.length ? ` The newest ${list.photos.length} lines are listed.` : ''}`;
  // One line per RECORD: the same image in two projects gives two lines.
  $('server-list').innerHTML = '<tr><th>record</th><th>state</th><th></th></tr>' +
    list.photos.map((e) => {
      const thumb = sibling(e.photo)?.thumb ?? (e.state === 'record only' ? '' : `/server-photo?photo=${encodeURIComponent(e.photo)}`);
      const label = e.state === 'photo only' ? 'a photo without its record yet'
        : `<strong>${escapeHtml(e.project)}</strong> ${escapeHtml(e.note || '—')}`;
      const shared = e.records > 1 ? ` <span class="muted">(the same image is in ${e.records} records)</span>` : '';
      const mine = e.id ? e.id === selected : e.photo === selectedName();
      return `
      <tr data-photo="${escapeHtml(e.photo)}" data-record="${escapeHtml(e.id ?? '')}" data-label="${escapeHtml(e.note || e.project || 'a photo without its record')}" class="${mine ? 'selected' : ''}">
        <td>${thumb ? viewLink(thumb, 'thumb') : ''}${label}${shared}</td>
        <td>${e.state}</td>
        <td><button class="del del-server" type="button">Delete on server</button></td>
      </tr>`;
    }).join('');
  for (const btn of $('server-list').querySelectorAll('.del-server')) {
    const tr = btn.closest('tr');
    btn.onclick = () => deleteServer(tr.dataset.photo, tr.dataset.record || null, tr.dataset.label);
  }
  // A stamp per redraw, so a reader (or a test) knows the list is fresh.
  $('server-list').dataset.at = String(Date.now());
}

async function refreshSelected() {
  const name = selectedName();
  if (!name) return;
  $('server-photo').textContent = name;
  try {
    const s = await (await fetch(`/server-state?photo=${encodeURIComponent(name)}`)).json();
    if (s.error) throw new Error(s.error);
    // The counts stay on the element as data, for the tests; the page shows the sentence.
    $('server-verdict').dataset.photoRows = s.photoRows;
    $('server-verdict').dataset.metaRows = s.metaRows;
    $('server-verdict').textContent = verdictOf(s);
    await refreshBlocks(s);
  } catch (err) {
    $('server-verdict').textContent = `The server did not answer: ${err.message}`;
  }
}

/**
 * One block per chunk of the selected photo: green when the server holds it, grey
 * when not. A photo saved on this phone asks by its own chunk hashes, so the blocks
 * show also a half-sent photo. A photo from the server asks by its manifest.
 */
async function refreshBlocks(state) {
  const photo = photos.get(selected);
  // After a reload the page knows no chunk hashes; the phone's stored bytes give them.
  if (photo && !photo.chunkHashes && photo.source === 'phone') await learnChunks(photo.name);
  const hashes = photo?.chunkHashes;
  if (!hashes && state.photoRows !== 1) {
    $('blocks').innerHTML = '';
    $('blocks-caption').textContent = '';
    return;
  }
  const name = photo?.name;
  if (!name) return;
  const res = await fetch('/server-blocks', { method: 'POST', body: JSON.stringify({ photo: name, hashes }) });
  const b = await res.json();
  if (name !== selectedName()) return; // another photo was selected meanwhile
  if (b.error) {
    $('blocks-caption').textContent = `The blocks are not known: ${b.error}`;
    return;
  }
  const held = b.held.filter(Boolean).length;
  $('blocks').innerHTML = b.held.map((h, i) => `<span class="${h ? 'held' : 'missing'}" title="chunk ${i + 1}"></span>`).join('');
  $('blocks-caption').textContent = `${held} of ${b.total} blocks are on the server.`
    + (held > 0 && held < b.total ? ' The next sync sends only the grey ones.' : '');
}

/** Ask the phone for the chunks of a stored photo, and give them to each of its records. */
async function learnChunks(name) {
  let c;
  try {
    c = await call('localChunks', name);
  } catch (err) {
    log(`The chunks of the photo are not known: ${escapeHtml(err.message)}`, 'err');
    return;
  }
  if (!c) return;
  for (const p of photos.values()) {
    if (p.name === name) Object.assign(p, { size: c.size, hash: c.hash, chunks: c.chunks.length, chunkHashes: c.chunks });
  }
  renderPhotos();
}

// ── the link ───────────────────────────────────────────────────────────────
async function control(body) {
  const res = await fetch('/control', { method: 'POST', body: JSON.stringify(body) });
  paintLink(await res.json());
}

function paintLink(s) {
  $('offline').checked = s.offline;
  for (const r of document.querySelectorAll('input[name=speed]')) r.checked = r.value === s.speed;
  $('arm-chunks-state').textContent = s.dropAfterChunks === null ? '' : `armed: drops after ${s.dropAfterChunks} more chunk(s)`;
  $('arm-reply-state').textContent = s.dropNextReply ? 'armed: the next push reply is lost' : '';
  paintCounters(s.counters);
}

/** The newest counters of the proxy, from /control and /events. */
let linkCounters = null;

function paintCounters(c) {
  linkCounters = c;
  $('c-chunks').textContent = c.chunksForwarded;
  $('c-bytes').textContent = kib(c.chunkBytesForwarded);
  $('c-pushes').textContent = c.pushesForwarded;
  $('c-drops').textContent = c.drops;
  $('c-replies').textContent = c.repliesDiscarded;
}

/**
 * The live blocks. kinedb has no event per stored chunk (a WATCH fires only when the
 * photo row commits), but it answers each PutFileChunk with FileChunkPut only AFTER
 * the chunk is stored. The proxy reports that answer; the page then asks kinedb
 * (FindHeldChunks) again, at most once per 300 ms.
 */
let blocksTimer = null;
function scheduleBlocks() {
  if (blocksTimer) return;
  blocksTimer = setTimeout(async () => {
    blocksTimer = null;
    if (selected) await refreshBlocks({ photoRows: 0 }).catch(() => {});
  }, 300);
}

function linkEvent(e) {
  const li = document.createElement('li');
  const t = new Date(e.t).toLocaleTimeString();
  if (e.kind === 'frame') {
    const arrow = e.dir === 'up' ? '→ server' : '← device';
    li.className = e.action ? 'err' : e.frames.some((f) => f.startsWith('PutFileChunk')) ? 'chunk' : '';
    li.textContent = `${t} ${arrow} ${e.frames.join(' ')} ${kib(e.bytes)}${e.action ? ` — ${e.action}` : ''}`;
    if (e.counters) paintCounters(e.counters);
    // The server confirmed a stored chunk: ask kinedb again which blocks it holds.
    if (e.dir === 'down' && e.frames.some((f) => f.startsWith('FileChunkPut'))) scheduleBlocks();
  } else if (e.kind === 'drop') {
    li.className = 'err';
    li.textContent = `${t} ✕ link ${e.link} dropped: ${e.reason}`;
    paintLink(e.state);
  } else if (e.kind === 'control') {
    paintLink(e.state);
    return;
  } else {
    li.className = 'muted';
    li.textContent = `${t} ${e.kind}${e.link ? ` link ${e.link}` : ''}${e.reason ? `: ${e.reason}` : ''}`;
  }
  const list = $('link-log');
  list.prepend(li);
  while (list.children.length > 200) list.lastChild.remove();
}

// ── start ──────────────────────────────────────────────────────────────────
async function boot() {
  $('capture').onsubmit = save;
  $('sync').onclick = sync;
  $('offline').onchange = (e) => control({ offline: e.target.checked });
  for (const r of document.querySelectorAll('input[name=speed]')) r.onchange = () => control({ speed: r.value });
  $('drop-now').onclick = () => fetch('/control/drop', { method: 'POST' });
  $('arm-chunks').onclick = () => control({ dropAfterChunks: Number($('arm-chunks-n').value) });
  $('arm-reply').onclick = () => control({ dropNextReply: true });
  $('disarm').onclick = () => control({ dropAfterChunks: null, dropNextReply: false });
  $('reset-counters').onclick = () => control({ resetCounters: true });
  $('server-refresh').onclick = refreshServer;
  $('autosync').checked = autosync();
  $('autosync').onchange = (e) => {
    setAutosync(e.target.checked);
    // Auto sync on: the page syncs by itself, so "Sync now" is disabled.
    $('sync').disabled = busy || e.target.checked;
    if (e.target.checked) autoSync();
    else stopLiveTail();
  };
  $('erase').onclick = eraseLocal;
  $('resend').onclick = resendRows;

  new EventSource('/events').onmessage = (ev) => linkEvent(JSON.parse(ev.data));
  paintLink(await (await fetch('/control')).json());

  $('status').textContent = 'Loading the engine and syncing the two tables…';
  try {
    const session = await (await fetch('/session')).json();
    if (session.error) throw new Error(session.error);
    cred = { url: session.url, token: session.token };
    const mode = await call('mode');
    $('storage').textContent = mode.memory
      ? 'The phone database is in memory (?memory=1): a reload loses it.'
      : 'The phone database is stored in this browser (OPFS). It survives a reload.';
    if (autosync() || !mode.hasTables) {
      const fresh = await call('start', cred, autosync());
      const words = { complete_as_of: 'complete', offline_stale: 'complete, and possibly behind', never_synced: 'not synced' };
      $('status').textContent = `Ready. The local copy of the server tables is ${words[fresh.photos.state] ?? fresh.photos.state}.${
        autosync() ? ' Auto sync is on.' : ' This was the first start of this phone database, so it synced once.'}`;
    } else {
      $('status').textContent = 'Ready. The list shows what the phone stored: no sync at the start. "Sync now" syncs.';
    }
    $('sync').disabled = autosync();
    $('save').disabled = false;
    await refreshPending();
    await loadFromLocal();
    selected = newest()[0]?.id ?? null;
    renderPhotos();
    await refreshServer();
    // Entries that waited before the reload go now, not at the next 15 s check.
    if (autosync() && (await call('pending')).length > 0) autoSync();
  } catch (err) {
    // A database that cannot open (an older build wrote it) is not a link problem: Erase is the way out.
    $('status').textContent = /cannot open the database/.test(err.message)
      ? `The start failed: ${err.message} Click "Erase the phone's database" twice, then reload the page. Photos that were never synced are lost.`
      : `The start failed: ${err.message}. Is the link online? Reload the page.`;
  }
}

boot();
